/**
 * Product audio:
 * - Completion cue: A2 three-pass soft nudge, synthesized with Web Audio.
 * - BGM: local MP3 loop controlled by the topbar background-music toggle.
 */

const BGM_ENABLED_KEY = 'gime5.bgmEnabled.v1';
const BGM_SRC = '/audio/gime5-bgm-spring-loop.mp3';
const BGM_VOLUME = 0.2;
const COMPLETION_GAIN = 10;
const MASCOT_PEEK_GAIN = 5;
const MASCOT_POP_GAIN = 5;

let ctx: AudioContext | null = null;
let bgmAudio: HTMLAudioElement | null = null;
let bgmUnlockRetryInstalled = false;

// Default ON for first-time users: bgm is part of the "calm desk" vibe and the
// audio context unlocks on the first interaction anyway (installBgmUnlockRetry).
// Only an explicit '0' (user toggled off) counts as disabled.
export function isBgmEnabled(): boolean {
  try {
    return localStorage.getItem(BGM_ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
}

function getBgmAudio(): HTMLAudioElement | null {
  if (bgmAudio) return bgmAudio;
  if (typeof Audio === 'undefined') return null;
  bgmAudio = new Audio(BGM_SRC);
  bgmAudio.loop = true;
  bgmAudio.preload = 'auto';
  bgmAudio.volume = BGM_VOLUME;
  return bgmAudio;
}

function installBgmUnlockRetry(): void {
  if (bgmUnlockRetryInstalled || typeof window === 'undefined') return;
  bgmUnlockRetryInstalled = true;

  const retry = () => {
    bgmUnlockRetryInstalled = false;
    window.removeEventListener('pointerdown', retry, true);
    window.removeEventListener('keydown', retry, true);
    void syncBgmPlayback();
  };

  window.addEventListener('pointerdown', retry, true);
  window.addEventListener('keydown', retry, true);
}

async function playBgm(): Promise<void> {
  const audio = getBgmAudio();
  if (!audio) return;
  audio.loop = true;
  audio.volume = BGM_VOLUME;

  try {
    await audio.play();
  } catch {
    installBgmUnlockRetry();
  }
}

function pauseBgm(): void {
  const audio = getBgmAudio();
  if (!audio) return;
  audio.pause();
}

export function setBgmEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(BGM_ENABLED_KEY, enabled ? '1' : '0');
  } catch {
    // no-op (Safari private mode etc.)
  }

  if (enabled) void playBgm();
  else pauseBgm();
}

export async function syncBgmPlayback(): Promise<void> {
  if (isBgmEnabled()) await playBgm();
  else pauseBgm();
}

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

function beep(c: AudioContext, freq: number, when: number, dur: number, peak = 0.15): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.frequency.value = freq;
  osc.type = 'sine';
  osc.connect(gain).connect(c.destination);
  const t0 = c.currentTime + when;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

function tone(
  c: AudioContext,
  freq: number,
  when: number,
  dur: number,
  peak: number,
  type: OscillatorType = 'sine',
): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  const t0 = c.currentTime + when;
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.018);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.04);
}

function softNoise(c: AudioContext, when: number, dur: number, peak: number, filterFreq = 2200): void {
  const len = Math.max(1, Math.floor(c.sampleRate * dur));
  const buffer = c.createBuffer(1, len, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < len; i += 1) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.2);
  }

  const source = c.createBufferSource();
  const filter = c.createBiquadFilter();
  const gain = c.createGain();
  const t0 = c.currentTime + when;

  source.buffer = buffer;
  filter.type = 'bandpass';
  filter.frequency.value = filterFreq;
  filter.Q.value = 0.9;
  gain.gain.setValueAtTime(peak, t0);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  source.connect(filter).connect(gain).connect(c.destination);
  source.start(t0);
  source.stop(t0 + dur);
}

function chirp(
  c: AudioContext,
  fromFreq: number,
  toFreq: number,
  when: number,
  dur: number,
  peak = 0.08,
  type: OscillatorType = 'sine',
): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.connect(gain).connect(c.destination);
  const t0 = c.currentTime + when;
  osc.frequency.setValueAtTime(fromFreq, t0);
  osc.frequency.linearRampToValueAtTime(toFreq, t0 + dur);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  osc.start(t0);
  osc.stop(t0 + dur + 0.04);
}

function playCompletionMotif(c: AudioContext, offset: number, scale = 1): void {
  const gain = scale * COMPLETION_GAIN;
  softNoise(c, offset, 0.035, 0.01 * gain);
  tone(c, 523.25, offset + 0.02, 0.18, 0.026 * gain, 'triangle');
  tone(c, 659.25, offset + 0.16, 0.24, 0.036 * gain, 'triangle');
  tone(c, 783.99, offset + 0.34, 0.38, 0.032 * gain, 'sine');
  tone(c, 1046.5, offset + 0.46, 0.26, 0.016 * gain, 'sine');
}

function playMascotPeekPattern(c: AudioContext): void {
  chirp(c, 520, 760, 0, 0.11, 0.045 * MASCOT_PEEK_GAIN, 'triangle');
  chirp(c, 760, 610, 0.09, 0.1, 0.035 * MASCOT_PEEK_GAIN, 'sine');
}

function playMascotPopPattern(c: AudioContext): void {
  chirp(c, 420, 700, 0, 0.1, 0.07 * MASCOT_POP_GAIN, 'triangle');
  chirp(c, 760, 580, 0.095, 0.11, 0.05 * MASCOT_POP_GAIN, 'sine');
  beep(c, 980, 0.205, 0.08, 0.035 * MASCOT_POP_GAIN);
}

function playMascotCue(pattern: (c: AudioContext) => void): void {
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === 'suspended') void c.resume();
    pattern(c);
  } catch {
    // Interaction sounds are decorative; never block the mascot state.
  }
}

/** A2 three-pass soft nudge for 5-minute round and segment completion. */
export function playTimerEndCue(): void {
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === 'suspended') void c.resume();
    playCompletionMotif(c, 0, 1);
    playCompletionMotif(c, 1.5, 0.82);
    playCompletionMotif(c, 3, 0.66);
  } catch {
    // fail silent — visual cue (title flash + full-screen card) still works
  }
}

/** Single-pass cue for manual "提前结束" — softer, no triple repetition. */
export function playTimerEarlyEndCue(): void {
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === 'suspended') void c.resume();
    playCompletionMotif(c, 0, 0.7);
  } catch {
    // fail silent
  }
}

/** One short "滴" for the exact moment a 5-minute round starts. */
export function playTimerStartCue(): void {
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === 'suspended') void c.resume();
    beep(c, 1046.5, 0, 0.08, 0.11);
    beep(c, 1568, 0.075, 0.055, 0.045);
  } catch {
    // fail silent
  }
}

export function playMascotPeekCue(): void {
  playMascotCue(playMascotPeekPattern);
}

export function playMascotPopCue(): void {
  playMascotCue(playMascotPopPattern);
}
